import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Form } from "antd"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import PropTypes from "prop-types"
import { TimezoneSelect } from "pagesV2/pro/Experiences/ListingScheduling/DaysAndHours/TimezoneSelect"
import { DaySelector } from "pagesV2/pro/Experiences/ListingScheduling/DaysAndHours/DaySelector"
import { TimeBlockForm } from "pagesV2/pro/Experiences/ListingScheduling/DaysAndHours/TimeBlockForm"
import { InfoTooltip } from "ui/Tooltip/InfoTooltip"

/**
 * Wizard-local copy of DaysAndHours.
 *
 * selectedDays and selectedTimezone are local UI state that DaySelector/TimezoneSelect
 * need to render correctly. They are kept in sync with the form via Form.useWatch —
 * this ensures that when WizardProvider calls form.setFieldsValue() after the async
 * trip load, the UI updates correctly even though the component has already mounted.
 */
export const DaysAndHours = ({ form }) => {
  const [t] = useTranslation()

  const [selectedTimezone, setSelectedTimezone] = useState(null)
  const [selectedDays, setSelectedDays] = useState([])

  // Watch form fields so local state stays in sync when WizardProvider
  // populates the form after the async trip fetch completes on refresh.
  const availableDaysWatch = Form.useWatch("available_days", form)
  const timeZoneWatch = Form.useWatch("time_zone", form)

  useEffect(() => {
    if (timeZoneWatch) setSelectedTimezone(timeZoneWatch)
  }, [timeZoneWatch])

  useEffect(() => {
    if (availableDaysWatch && typeof availableDaysWatch === "object" && !Array.isArray(availableDaysWatch)) {
      const activeDays = Object.keys(availableDaysWatch).filter((day) => availableDaysWatch[day])
      setSelectedDays(activeDays)
    }
  }, [availableDaysWatch])

  const handleTimezoneChange = (value) => {
    setSelectedTimezone(value)
    form.setFieldsValue({ time_zone: value })
  }

  useEffect(() => {
    const availableDays = form.getFieldValue("available_days")
    let hasAvailableDays = false
    const hasSelectedDays = selectedDays && selectedDays.length > 0

    if (Array.isArray(availableDays)) {
      hasAvailableDays = availableDays.length > 0
    } else if (availableDays && typeof availableDays === "object") {
      hasAvailableDays = Object.values(availableDays).some(Boolean)
    }

    if (!hasAvailableDays && !hasSelectedDays) {
      form.setFields([{ name: "available_days", errors: [t("experiences.scheduling.errors.day_and_time")] }])
    } else {
      form.setFields([{ name: "available_days", errors: [] }])
    }
  }, [form, selectedDays, t])

  return (
    <Card
      title={
        <div className="align-center gap-xs">
          {t("experiences.scheduling.sections.days_and_hours.title")}
          <InfoTooltip title={t("experiences.scheduling.sections.days_and_hours.tooltip")} />
        </div>
      }
      className="mb-3xl"
    >
      <Form.Item name="time_zone" label={t("experiences.scheduling.labels.timezone")} rules={[{ required: true }]}>
        <TimezoneSelect className="mb-l" onChange={handleTimezoneChange} value={selectedTimezone} />
      </Form.Item>
      <Text className="block mb-s" type="subtitle" fw={500}>
        {t("experiences.scheduling.labels.day_and_time")}
      </Text>
      <Form.Item name="available_days" className="mb-xxl">
        <DaySelector form={form} selectedDays={selectedDays} setSelectedDays={setSelectedDays} asObject={true} />
      </Form.Item>
      {selectedDays.length > 0 && <TimeBlockForm day={selectedDays[0]} form={form} />}
    </Card>
  )
}

DaysAndHours.propTypes = {
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func,
    setFields: PropTypes.func
  }).isRequired
}
